import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useCanBlockNumber } from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import {
  CopyLinkToClipboard,
  NewCall,
  NewFax,
  NewText,
} from '~/components/compositional/QuickActions'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import { EmptyDetailView } from '~/components/shared/EmptyDetailView'
import { BlockContactQuickActionButton } from '~/components/specialized/BlockedNumber/BlockContactQuickActionButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useClipboard } from '~/hooks/useClipboard'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { useDebounce } from '~/hooks/useDebounce'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  ContactsDetailView,
  DetailViewHeader,
} from '~/pages/Contacts/ContactsDetailView'
import { ContactsListView } from '~/pages/Contacts/ContactsListView'
import {
  ContactActions,
  ContactDetails,
  ContactHistory,
  ContactNumber,
  useContactsViewModel,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { AnalyticsNamespace, ContactsAnalyticsEvents } from '~/types/Analytics'
import { SearchValue } from '~/types/Shared'
import { noop } from '~/utils/functions'
import { getShareableWebAppUrl } from '~/utils/shareableUrl'

import { Contact, ContactSource } from '@mangovoice/types'
import { ResourceLoadingState } from '@mangovoice/types'

const DEBOUNCE_WAIT_MS = 500

const contactHistory: ContactHistory[] = [
  {
    id: 1,
    date: new Date('2/13/24 1:42 AM'),
    type: 'messaging',
    toNumber: {
      id: 1,
      label: 'mobile',
      number: '+1221234567',
      availableActions: [],
      availableQuickActions: [],
    },
  },
  {
    id: 2,
    date: new Date('2/12/24 3:08 PM'),
    type: 'inboundCall',
    fromNumber: {
      id: 2,
      label: 'mobile',
      number: '+1221234567',
      availableActions: [],
      availableQuickActions: [],
    },
    toNumber: '+1224552233',
  },
  {
    id: 3,
    date: new Date('2/11/24 4:32 PM'),
    type: 'outboundCall',
    fromNumber: '+1224552233',
    toNumber: {
      id: 3,
      label: 'mobile',
      number: '+1221234567',
      availableActions: [],
      availableQuickActions: [],
    },
  },
  {
    id: 4,
    date: new Date('2/6/24 11:12 AM'),
    type: 'outboundFax',
    fromNumber: '+1224552233',
    toNumber: {
      id: 4,
      label: 'fax',
      number: '+1221234567',
      availableActions: [],
      availableQuickActions: [],
    },
  },
  {
    id: 5,
    date: new Date('2/5/24 9:06 AM'),
    type: 'voicemail',
    fromNumber: {
      id: 5,
      label: 'mobile',
      number: '+1221234567',
      availableActions: [],
      availableQuickActions: [],
    },
    toNumber: '+1224552233',
  },
]

export const ContactsPage = () => {
  const { state, actions } = useContactsViewModel()
  const { contactId } = useParams()
  const navigate = useNavigate()
  const { copyToClipboard } = useClipboard()
  const { t } = useTranslation()
  const showTexting = useShowTexting()

  const {
    callModal: [, setCallModalState],
    faxModal: [faxModalState, setFaxModalState],
    messagingModal: [, setMessagingModalState],
  } = useModalState()

  const analytics = useAnalytics(AnalyticsNamespace.CONTACTS)

  const {
    state: { isOpen: isListPanelOpen },
    togglePanelVisibility,
  } = useTogglePanel()
  useEffect(() => {
    if (!state.selectedContactId && !isListPanelOpen) {
      togglePanelVisibility(true)
    }
  }, [])

  const phoneNumbers = useMemo(() => {
    return state.selectedContactDetails?.numbers?.map((n) => n.number)
  }, [state.selectedContactDetails])
  const canBlockNumber = useCanBlockNumber(phoneNumbers)

  const handleContactSourceSelected = (contactSource?: ContactSource) => {
    //undefined means no contact source selected or "All Contacts" selected (no source / default)
    actions.setSelectedContactSource(contactSource)
  }
  useEffect(() => {
    if (contactId) {
      togglePanelVisibility(false)
      actions.setSelectedContactId(contactId, true)
      analytics.track(ContactsAnalyticsEvents.ContactSelected)
    }
  }, [contactId])
  const handleContactSelected = (contact: Contact) => {
    navigate(`${ApplicationRoutes.CONTACTS}/${contact?.id}`)
  }

  const handleScrollEndReached = () => {
    actions.getNextContactsPage()
  }

  const handleMessagingClick = (number: string) => {
    setMessagingModalState({
      open: true,
      number,
    })
    analytics.track(ContactsAnalyticsEvents.ContactQuickActionClicked_Message)
  }
  const handleCallStartClick = (callee: {
    contactNumber: ContactNumber
    contact: ContactDetails
  }) => {
    setCallModalState({
      open: true,
      callee,
    })
    analytics.track(ContactsAnalyticsEvents.ContactQuickActionClicked_Call)
  }
  const handleFaxClick = (number: string) => {
    setFaxModalState({
      ...faxModalState,
      open: true,
      number,
    })
    analytics.track(ContactsAnalyticsEvents.ContactQuickActionClicked_Fax)
  }
  const handleCopyLink = () => {
    copyToClipboard(
      getShareableWebAppUrl(`/contacts/${state.selectedContactInstance?.id}`),
      noop,
      noop,
    )
    analytics.track(ContactsAnalyticsEvents.ContactQuickActionClicked_Link)
  }

  const [debounceSearch, setDebounceSearch] = useState<SearchValue>(
    SearchValue.empty,
  )
  //useDebounce fires on mount too; only requery once the user has typed
  const hasUserChangedSearch = useRef(false)

  useDebounce(
    () => {
      if (!hasUserChangedSearch.current) return
      actions.setSearchValue(debounceSearch, true)
    },
    DEBOUNCE_WAIT_MS,
    [debounceSearch],
  )

  const handleSearchTermChanged = (searchValue: SearchValue) => {
    hasUserChangedSearch.current = true
    setDebounceSearch(searchValue)
  }

  //on mount, fetch contact sources - on unmount, reset view model state
  useEffect(() => {
    actions.getContactSources()
    //the view model state is shared (e.g. with the webphone), so clear any
    //leftover search and list data before the initial fetch
    actions.setSearchValue(SearchValue.empty, false)
    actions.getContacts(0, true)

    return () => {
      actions.resetState()
    }
  }, [])

  type QuickActionTypes = ContactActions | 'link' | 'block'
  const quickActionButtonMap: Record<QuickActionTypes, React.ReactElement> = {
    call: (
      <NewCall
        key="call"
        onClick={() => {
          if (!state.selectedContactDetails) return
          if (!state.selectedContactDetails.defaultNumber) return
          handleCallStartClick({
            contactNumber: state.selectedContactDetails.defaultNumber,
            contact: state.selectedContactDetails,
          })
        }}
      />
    ),
    message: (
      <NewText
        key="message"
        onClick={() => {
          handleMessagingClick(
            state.selectedContactDetails?.defaultNumber?.number ?? '',
          )
        }}
      />
    ),
    fax: (
      <NewFax
        key="fax"
        onClick={() => {
          handleFaxClick(
            state.selectedContactDetails?.defaultNumber?.number ?? '',
          )
        }}
      />
    ),
    link: <CopyLinkToClipboard key="link" onCopyLink={handleCopyLink} />,
    block: (
      <BlockContactQuickActionButton
        key="block"
        numbers={state.selectedContactDetails?.numbers ?? []}
      />
    ),
  }

  const getDetailView = () => {
    let detailView: React.ReactNode
    let detailHeader: React.ReactNode
    let quickActionButtons: React.ReactElement[] | undefined

    if (state.selectedContactDetails !== undefined) {
      detailHeader = (
        <DetailViewHeader
          contactName={`${state.selectedContactDetails.name}`}
          age={state.selectedContactDetails.age}
          gender={state.selectedContactDetails.gender}
        />
      )

      detailView = (
        <ContactsDetailView
          contactDetails={state.selectedContactDetails}
          searchTerm={state.searchValue?.value}
          contactHistory={contactHistory}
          onMessagingClick={(number) => {
            setMessagingModalState({
              open: true,
              number,
            })
            analytics.track(
              ContactsAnalyticsEvents.ContactDetailActionClicked_Message,
            )
          }}
          onCallClick={(callee) => {
            setCallModalState({
              open: true,
              callee,
            })
            analytics.track(
              ContactsAnalyticsEvents.ContactDetailActionClicked_Call,
            )
          }}
          onFaxClick={(number) => {
            setFaxModalState({
              ...faxModalState,
              open: true,
              number,
            })
            analytics.track(
              ContactsAnalyticsEvents.ContactDetailActionClicked_Fax,
            )
          }}
          onCopyClick={(number) => {
            copyToClipboard(number, noop, noop)
            analytics.track(
              ContactsAnalyticsEvents.ContactDetailActionClicked_Copy,
            )
          }}
        />
      )

      quickActionButtons = new Array<React.ReactElement>()
      if (state.selectedContactDetails.defaultNumber?.availableQuickActions) {
        state.selectedContactDetails.defaultNumber?.availableQuickActions
          .filter((action) => showTexting || action !== 'message')
          .forEach((action) => {
            quickActionButtons?.push(quickActionButtonMap[action])
          })
      } else {
        quickActionButtons.push(quickActionButtonMap.call)
        if (showTexting) {
          quickActionButtons.push(quickActionButtonMap.message)
        }
        quickActionButtons.push(quickActionButtonMap.fax)
      }
      quickActionButtons.push(quickActionButtonMap['link'])
      if (canBlockNumber) {
        quickActionButtons.push(quickActionButtonMap['block'])
      }
    } else {
      // Empty State
      detailHeader = null

      detailView = (
        <EmptyDetailView message={t('contactsPage.emptyDetailMessage')} />
      )

      quickActionButtons = undefined
    }

    return { detailHeader, detailView, quickActionButtons }
  }

  const {
    detailHeader: DetailHeader,
    detailView: DetailView,
    quickActionButtons: QuickActionButtons,
  } = getDetailView()

  return (
    <ResponsivePage
      listPanel={
        <ContactsListView
          contactSources={state.contactSources}
          contacts={state.contactsListState.data}
          contactsLoading={
            !state.contactsListLoadingState?.state ||
            state.contactsListLoadingState.state ===
              ResourceLoadingState.Loading
          }
          selectedContactSource={state.contactSources?.find(
            (cs) => cs.id === state.selectedContactSourceId,
          )}
          selectedContactId={state.selectedContactId}
          searchValue={state.searchValue}
          onContactSourceSelected={handleContactSourceSelected}
          onContactSelected={handleContactSelected}
          onScrollEndReached={handleScrollEndReached}
          onSearchTermChanged={handleSearchTermChanged}
          autoScrollList={!!contactId}
        />
      }
      header={DetailHeader}
      quickActionButtons={QuickActionButtons}
      detailPanel={DetailView}
      backgroundColor="bg.subtle"
    />
  )
}
